import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { SubmissionError, reset, getFormValues } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';

import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { actions as rootActions } from '../../root';
import { create as createPolling } from '../../../../../shared/js/intervals';
import createCloseForm from '../components/close-form';
import { prepareTicketsList } from '../../shared/thread-utils';

const threadPolling = createPolling('thread', {
	newMessages: { interval: 5000 }
});

const closeForm = createCloseForm({
	namespace: 'extThreadsItem',
	stateNodeName: 'closeExtThreadForm'
});

// actions types
const FETCH_DATA_SUCCESS = 'ext-threads-item/fetch-data-success';
const FETCH_ANSWERS_SUCCESS = 'ext-threads-item/fetch-answers-success';
const SUBMIT_ANSWER_SUCCESS = 'ext-threads-item/submit-answer-success';
const SUBMIT_ANSWER_ERROR = 'ext-threads-item/submit-answer-error';
const REQUEST_ERROR = 'ext-threads-item/request-error';

const TOGGLE_PROGRESS_BAR = 'ext-threads-item/toggle-progress-bar';
const TICKET_CLOSE_SUCCESS = 'ext-threads-item/ticket-close-success';

const FETCH_SEARCH_THREADS = 'ext-threads-item/fetch-search-threads';

// permissions
export const allowedPermissions = {
	view: 'ext_threads_list',
	answer: 'ext_threads_answer',
	closeTicket: 'ext_threads_close'
};

// initial state
const initialState = {
	data: null,
	answerFormInitialValues: {
		text: null
	},
	search: null,
	showProgressBar: false,
	closeExtThreadForm: closeForm.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => dispatch => {
			const ticketListQueryContext = window.location && window.location.search || '';
			const request = http({
				url: [
					`${endpoints.EXTERNAL_THREADS.LIST}/${props.params.id}`,
					`${endpoints.EXTERNAL_THREADS.LIST}?${ticketListQueryContext}`
				],
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
			});

			request(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					threadPolling.actions.start(
						'thread',
						'newMessages',
						actions.fetchAnswers({ params: { id: props.params.id } }),
						dispatch
					);
					dispatch(rootActions.fetchUnreadPosts('external'));
				}
			});
		},

		fetchAnswers: props => dispatch => {
			const request = http({
				url: `${endpoints.EXTERNAL_THREADS.LIST}/${props.params.id}`,
				method: 'get',
				actions: [FETCH_ANSWERS_SUCCESS, REQUEST_ERROR]
			});

			request(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					threadPolling.actions.start(
						'thread',
						'newMessages',
						actions.fetchAnswers({ params: { id: props.params.id } }),
						dispatch
					);
				}
			});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		// @todo after implement internal page progressbar rewrite logic
		switchToTicket: ticketId => dispatch => {
			dispatch(push(`external-tickets/${ticketId}`));
		},

		submitAnswer: data => (dispatch, getState) => {
			const { ticket: { id, email } } = getState().extThreadsItem.data;

			const request = http({
				url: `${endpoints.EXTERNAL_THREADS.LIST}/${id}`,
				method: 'post',
				actions: [SUBMIT_ANSWER_SUCCESS, SUBMIT_ANSWER_ERROR],
				data: {
					text: data.text || '',
					email
				}
			});

			return request(dispatch).then(action => {
				if (action.type === SUBMIT_ANSWER_SUCCESS) {
					dispatch(reset('extSupportForm'));
					dispatch(actions.fetchData({ params: { id } }));
				}
				if (action.type === SUBMIT_ANSWER_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
		},

		close: (threadIds = [], closeMessage = '') => (dispatch, getState) => {
			const { data: { ticket } } = getState().extThreadsItem;
			const url = threadIds.map(id => endpoints.EXTERNAL_THREADS.CLOSE.replace('{id}', id));

			dispatch(actions.toggleProgressBar());

			const request = http({
				url,
				method: 'put',
				actions: [TICKET_CLOSE_SUCCESS, REQUEST_ERROR],
				data: {
					closeMessage
				}
			});

			request(dispatch).then(action => {
				dispatch(actions.toggleProgressBar());
				if (action.type === TICKET_CLOSE_SUCCESS) {
					dispatch(actions.fetchData({ params: { id: ticket.id } }));
				}
			});
		},

		search: () => (dispatch, getState) => {
			const searchForm = getFormValues('extThreadsItemSearchForm')(getState());
			const search = searchForm && searchForm.search;

			http({
				url: `${endpoints.EXTERNAL_THREADS.LIST}?search=${search || ''}`,
				method: 'get',
				actions: [FETCH_SEARCH_THREADS, REQUEST_ERROR]
			})(dispatch);
		}
	},
	closeForm.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			threadPolling.actions.stop('thread', 'newMessages');
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const [ticket, ticketsList] = action.payload.map(entity => entity.data);
			return {
				...state,
				data: {
					ticket,
					ticketsList
				},
				error: false
			};
		},
		[FETCH_ANSWERS_SUCCESS]: (state, action) => {
			const ticket = action.payload.data;
			return {
				...state,
				data: {
					...state.data,
					ticket
				}
			};
		},
		[FETCH_SEARCH_THREADS]: (state, action) => {
			const threads = action.payload.data;
			return {
				...state,
				data: {
					...state.data,
					ticketsList: threads
				}
			};
		},
		[REQUEST_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
	},
	closeForm.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.extThreadsItem.data && state.extThreadsItem.data['ticket'],
	getTicketsList: state => state.extThreadsItem.data && prepareTicketsList(state, 'extThreads', 'extThreads'),
	getAnswerFormInitialValues: state => state.extThreadsItem.answerFormInitialValues,
	getIntl: state => intl(state),
	getProgressBar: state => state.extThreadsItem.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getThreadStatusesList: state => state.root.lists['thread-statuses'],
	getCloseFormInitialValues: state => closeForm.selectors.getInitialValues(state),
	getCloseFormShouldShowStatus: state => closeForm.selectors.getShouldShowCloseFormStatus(state),
	getCloseFormValidate: state => closeForm.selectors.getCloseFormValidate(state),
	getAdminTimezone: state => state.root.data.timezone,
	getError: state => state.extThreadsItem.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/external-threads/item/index.js